import { Component, type ErrorInfo, type ReactNode } from 'react';
import styles from './ErrorBoundary.module.css';

interface ErrorBoundaryProps {
  /** Optionaler Label für den Fallback (z.B. "Plan-Seite", "Karte"). */
  label?: string;
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

// Erkennt fehlgeschlagene dynamische Imports (Lazy-Chunks). Tritt typischerweise
// auf, wenn nach einem Deploy die content-gehashten Chunk-Dateinamen wechseln und
// ein bereits geöffneter Tab noch die alten Namen anfordert -> 404.
const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError/i;

function isChunkLoadError(error: Error | null): boolean {
  if (!error) return false;
  return error.name === 'ChunkLoadError' || CHUNK_ERROR_RE.test(error.message || '');
}

// Zeit-Guard gegen Reload-Schleifen: nach einem erfolgreichen Reload darf ein
// späterer Deploy erneut reloaden, aber ein sofortiger Fehlschlag (< 10s) führt
// nicht in eine Endlosschleife.
const RELOAD_TS_KEY = 'spa:lastChunkReload';
const RELOAD_DEBOUNCE_MS = 10_000;

function canAutoReload(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_TS_KEY) || 0);
    return Date.now() - last > RELOAD_DEBOUNCE_MS;
  } catch {
    // sessionStorage blockiert (Privatmodus o.ä.) -> kein Auto-Reload, sonst
    // könnten wir ohne Guard in eine Schleife geraten.
    return false;
  }
}

/**
 * App-weiter React-ErrorBoundary. Fängt synchrone Render-Errors und
 * Lifecycle-Errors aus dem Sub-Baum ab, damit ein einzelner Bug nicht
 * die ganze App auf eine leere Seite kippt.
 *
 * Hooks-Tip: dies ist EINE der wenigen Stellen, wo wir bewusst eine
 * Class-Component nutzen — React bietet kein Hook-Äquivalent für
 * `componentDidCatch` / `getDerivedStateFromError`.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Console-Log für Dev-Tooling. Im Production-Mode landet das im
    // Browser-Log und kann bei Bedarf über Sentry o.ä. abgegriffen
    // werden — für den MVP reicht der reine Trace.
    console.error('[ErrorBoundary] caught render-error', error, info.componentStack);

    // Stale-Deploy-Recovery: Schlägt ein Lazy-Chunk fehl (alter Tab, neuer
    // Deploy), laden wir die Seite einmalig automatisch neu — das frische
    // index.html referenziert die aktuellen Chunk-Hashes.
    if (isChunkLoadError(error) && canAutoReload()) {
      try { sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now())); } catch {}
      window.location.reload();
    }
  }

  handleReset = (): void => {
    // Soft-Reset: nur den lokalen Error-State zurücksetzen.
    this.setState({ error: null });
  };

  handleReload = (): void => {
    // Hard-Reload: ganzen Bundle neu laden (löst z.B. Code-Split-Chunks,
    // die durch ein Deploy invalidiert wurden).
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    const chunkError = isChunkLoadError(this.state.error);

    // Erster Render nach einem Chunk-Fehler: componentDidCatch löst gleich
    // window.location.reload() aus. Statt der erschreckenden Fehler-Box zeigen
    // wir eine neutrale Lade-Meldung (kein Flash), bis der Reload greift.
    if (chunkError && canAutoReload()) {
      return (
        <div className={styles.box} role="status">
          <div className={styles.icon} aria-hidden="true">⏳</div>
          <h2 className={styles.title}>Neue Version verfügbar</h2>
          <p className={styles.detail}>Wird geladen …</p>
        </div>
      );
    }

    const label = this.props.label ? ` (${this.props.label})` : '';

    // Chunk-Fehler, aber Auto-Reload ist (gerade eben) schon gelaufen: jetzt
    // hilft nur ein harter Reload — Soft-Reset würde den fehlenden Chunk nicht
    // beheben, daher blenden wir ihn aus.
    if (chunkError) {
      return (
        <div className={styles.box} role="alert">
          <div className={styles.icon} aria-hidden="true">⚠️</div>
          <h2 className={styles.title}>Neue Version verfügbar{label}</h2>
          <p className={styles.detail}>
            Eine neue Version der App ist verfügbar. Bitte lade die Seite neu.
          </p>
          <div className={styles.actions}>
            <button type="button" className={styles.primary} onClick={this.handleReload}>
              Seite neu laden
            </button>
          </div>
        </div>
      );
    }

    const message = this.state.error.message || 'Unbekannter Fehler';

    return (
      <div className={styles.box} role="alert">
        <div className={styles.icon} aria-hidden="true">⚠️</div>
        <h2 className={styles.title}>Etwas ist schiefgegangen{label}</h2>
        <p className={styles.detail}>{message}</p>
        <div className={styles.actions}>
          <button type="button" className={styles.primary} onClick={this.handleReset}>
            Erneut versuchen
          </button>
          <button type="button" className={styles.secondary} onClick={this.handleReload}>
            Seite neu laden
          </button>
        </div>
      </div>
    );
  }
}
